/**
 * PrescriptionMessageViewer.tsx
 *
 * Shows one stored pharmacy message (an NCPDP message ScriptSure relayed) as
 * it was received, and lets staff download it (plan R8, KTD12). Opened from
 * the patient Prescriptions tab's list.
 *
 * The body is untrusted text from outside Prospera. It is shown only as text
 * in a <pre>: never through dangerouslySetInnerHTML, an iframe, or a file URL
 * the browser could navigate to, so markup inside it (an XHTML <script>, say)
 * is never run or rendered. The server has already masked the header's
 * network sign-in values; the download saves exactly the body shown.
 *
 * The signature summary reports what the message carries and nothing more.
 * Prospera does not check signatures, so the words never say a message is
 * signed, unsigned, valid or verified: they list the elements found, or say
 * the delivered message contains no signature information.
 *
 * The download is a Blob typed application/octet-stream, so the browser
 * saves it instead of opening it, named .json for a message in the converted
 * JSON form and .xml otherwise.
 */
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { formatDateTime } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  getDawMessage,
  type DawPharmacyMessage,
  type DawPharmacyMessageSummary,
} from '@/services/prescriptions.service';

/** ScriptSure's message types, in words staff use. An unknown type shows as sent. */
const MESSAGE_TYPE_LABELS: Record<string, string> = {
  NewRx: 'New prescription',
  RxRenewalRequest: 'Renewal request',
  RxRenewalResponse: 'Renewal response',
  RxChangeRequest: 'Change request',
  RxChangeResponse: 'Change response',
  CancelRx: 'Cancel request',
  CancelRxResponse: 'Cancel response',
  RxFill: 'Fill notice',
  RxFillIndicatorChange: 'Fill notice change',
  Status: 'Status',
  Error: 'Error',
  Verify: 'Confirmation',
  RxHistoryRequest: 'Medication history request',
  RxHistoryResponse: 'Medication history',
};

/** A message whose type could not be read still opens; it is named plainly. */
const UNNAMED_MESSAGE = 'Pharmacy message';

export function pharmacyMessageLabel(type: string): string {
  if (type === '') return UNNAMED_MESSAGE;
  return MESSAGE_TYPE_LABELS[type] ?? type;
}

export function pharmacyMessageDirection(direction: DawPharmacyMessageSummary['direction']): string {
  if (direction === 'outbound') return 'Sent';
  if (direction === 'inbound') return 'Received';
  return '';
}

const LOAD_FAILED_TEXT =
  "Couldn't open this pharmacy message. Try again, and contact support if it keeps happening.";

const NO_SIGNATURE_TEXT = 'The delivered message contains no signature information.';

const BYTE_ORDER_MARK = 0xfeff;

/**
 * .json for the converted JSON form, .xml otherwise. Read as the server's
 * reader decides the format: the first character after an optional
 * byte-order mark and any whitespace.
 */
function downloadExtension(body: string): 'json' | 'xml' {
  const text = body.charCodeAt(0) === BYTE_ORDER_MARK ? body.slice(1) : body;
  const first = text.trimStart().charAt(0);
  return first === '{' || first === '[' ? 'json' : 'xml';
}

function downloadMessage(message: DawPharmacyMessage) {
  const blob = new Blob([message.body], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `pharmacy-message-${message.id}.${downloadExtension(message.body)}`;
  link.click();
  URL.revokeObjectURL(url);
}

function SignatureSummary({ signature }: { signature: DawPharmacyMessage['signature'] }) {
  if (!signature.found || signature.elements.length === 0) {
    return <p className="text-sm text-slate-600">{NO_SIGNATURE_TEXT}</p>;
  }
  return (
    <div className="space-y-1 text-sm text-slate-700">
      <p>Signature information in the delivered message:</p>
      <ul className="space-y-0.5 pl-4">
        {signature.elements.map((element, index) => (
          <li key={`${element.path}-${index}`} className="list-disc">
            <span className="font-medium">{element.name}</span>{' '}
            <span className="break-all text-xs text-slate-500">{element.path}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-slate-500">
        Listed as delivered. Prospera does not check whether a signature is valid.
      </p>
    </div>
  );
}

interface PrescriptionMessageViewerProps {
  patientId: number;
  /** The stored message to show, or null while the viewer is shut. */
  messageId: number | null;
  onClose: () => void;
}

export function PrescriptionMessageViewer({ patientId, messageId, onClose }: PrescriptionMessageViewerProps) {
  const query = useQuery({
    queryKey: ['daw-pharmacy-message', patientId, messageId],
    queryFn: () => getDawMessage(patientId, messageId as number),
    enabled: messageId != null,
    // A stored message never changes, so nothing needs to fetch it again.
    staleTime: Infinity,
    retry: false,
  });

  const message = messageId != null ? query.data : undefined;
  const direction = message ? pharmacyMessageDirection(message.direction) : '';

  return (
    <Modal
      isOpen={messageId != null}
      onClose={onClose}
      title={message ? pharmacyMessageLabel(message.type) : UNNAMED_MESSAGE}
      size="xl"
      footer={
        message ? (
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Download className="h-3.5 w-3.5" />}
            onClick={() => downloadMessage(message)}
          >
            Download
          </Button>
        ) : undefined
      }
    >
      {query.isLoading && (
        <div className="flex items-center gap-2 p-4 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading message
        </div>
      )}

      {query.isError && (
        <div className="space-y-2 p-4">
          <p className="text-sm text-rose-600">{getErrorMessage(query.error, LOAD_FAILED_TEXT)}</p>
          <Button size="sm" variant="secondary" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </div>
      )}

      {message && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            {direction ? `${direction} ` : ''}
            {formatDateTime(message.at)}
          </p>
          <SignatureSummary signature={message.signature} />
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-all rounded-md bg-slate-50 p-3 font-mono text-xs text-slate-800">
            {message.body}
          </pre>
        </div>
      )}
    </Modal>
  );
}
